import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from '@mui/stylis-plugin-rtl';
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import i18n from './i18n';

const THEME_STORAGE_KEY = 'calmpi_theme';
export const THEME_MODE_SELECTION_ENABLED = false;
const rtlCache = createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] });
const ltrCache = createCache({ key: 'mui-ltr' });
const ThemeModeContext = createContext(null);

const getInitialMode = () => {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const useThemeMode = () => useContext(ThemeModeContext);

export default function AppProviders({ children }) {
  const [mode, setMode] = useState(getInitialMode);
  const [language, setLanguage] = useState(i18n.resolvedLanguage || i18n.language || 'en');
  const direction = language === 'he' ? 'rtl' : 'ltr';
  const activeMode = THEME_MODE_SELECTION_ENABLED ? mode : 'light';

  useEffect(() => {
    const handleLanguageChange = (nextLanguage) => setLanguage(nextLanguage);
    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('data-theme', activeMode);
    document.body.setAttribute('dir', direction);
  }, [activeMode, direction, language]);

  const theme = useMemo(() => createTheme({
    direction,
    palette: {
      mode: activeMode,
      primary: { main: '#1677ff' },
      background: activeMode === 'dark'
        ? { default: '#111917', paper: '#18221f' }
        : { default: '#f4f0e9', paper: '#ffffff' },
      text: activeMode === 'dark'
        ? { primary: '#f3f7f6', secondary: '#a9b8b4' }
        : { primary: '#15211f', secondary: '#6e7d7a' },
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: 'Heebo, Arial, sans-serif',
      button: { fontWeight: 800, textTransform: 'none' },
    },
  }), [activeMode, direction]);

  const value = useMemo(() => ({
    mode: activeMode,
    toggleMode: () => setMode((current) => {
      const next = current === 'dark' ? 'light' : 'dark';
      localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    }),
  }), [activeMode]);

  return (
    <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ThemeModeContext.Provider value={value}>
          {children}
        </ThemeModeContext.Provider>
      </ThemeProvider>
    </CacheProvider>
  );
}
